<template>
  <div class="login-page">
    <el-form
      ref="registerForm"
      :model="form"
      :rules="rules"
      class="login-form-wrap"
      @submit.native.prevent
    >
      <div class="login-content">
        <h1 class="login-title">
          注册
        </h1>
        <p class="login-subtitle">创建账号，让收藏用得上</p>
        <div class="login-form">
          <el-form-item prop="username">
            <el-input
              v-model="form.username"
              placeholder="用户名"
              size="large"
              clearable
              class="login-input"
              @keyup.enter.native="handleRegister"
            >
              <i slot="prefix" class="el-input__icon el-icon-user"></i>
            </el-input>
          </el-form-item>
          <el-form-item prop="phone" class="register-phone-item">
            <div
              class="register-phone-field"
              :class="{ 'is-focused': phoneFieldFocused }"
            >
              <i class="el-icon-mobile-phone register-phone-field__icon"></i>
              <el-select
                v-model="form.countryCode"
                size="large"
                filterable
                class="register-country-select"
                popper-class="register-country-dropdown"
                @change="onCountryCodeChange"
                @visible-change="onCountrySelectVisible"
              >
                <el-option
                  v-for="item in countryOptions"
                  :key="item.code + '-' + item.label"
                  :label="item.dialLabel"
                  :value="item.code"
                >
                  <span class="register-country-option__label">{{ item.label }}</span>
                  <span class="register-country-option__dial">{{ item.dialLabel }}</span>
                </el-option>
              </el-select>
              <span class="register-phone-field__divider" aria-hidden="true"></span>
              <el-input
                v-model.trim="form.phone"
                placeholder="请输入手机号"
                size="large"
                clearable
                class="register-phone-input"
                maxlength="20"
                @input="onPhoneInput"
                @focus="phoneFieldFocused = true"
                @blur="phoneFieldFocused = false"
                @keyup.enter.native="handleRegister"
              />
            </div>
          </el-form-item>
          <el-form-item prop="password">
            <el-input
              v-model="form.password"
              type="password"
              placeholder="密码"
              size="large"
              show-password
              clearable
              class="login-input"
              @keyup.enter.native="handleRegister"
            >
              <i slot="prefix" class="el-input__icon el-icon-lock"></i>
            </el-input>
          </el-form-item>
          <el-form-item prop="confirmPassword">
            <el-input
              v-model="form.confirmPassword"
              type="password"
              placeholder="确认密码"
              size="large"
              show-password
              clearable
              class="login-input"
              @keyup.enter.native="handleRegister"
            >
              <i slot="prefix" class="el-input__icon el-icon-lock"></i>
            </el-input>
          </el-form-item>
          <el-form-item prop="email">
            <el-input
              v-model.trim="form.email"
              placeholder="邮箱地址"
              size="large"
              clearable
              class="login-input"
            >
              <i slot="prefix" class="el-input__icon el-icon-message"></i>
            </el-input>
          </el-form-item>
          <el-form-item prop="emailCode" class="register-code-item">
            <div class="register-code-row">
              <el-input
                v-model.trim="form.emailCode"
                placeholder="邮箱验证码"
                size="large"
                clearable
                class="login-input register-code-input"
                @keyup.enter.native="handleRegister"
              >
                <i slot="prefix" class="el-input__icon el-icon-key"></i>
              </el-input>
              <el-button
                type="primary"
                class="register-send-code-btn"
                :disabled="!canSendCode"
                :loading="sendCodeLoading"
                @click="handleSendEmailCode"
              >
                {{ sendCodeText }}
              </el-button>
            </div>
          </el-form-item>
          <el-form-item>
            <el-button
              type="primary"
              class="login-btn"
              :loading="loading"
              :disabled="!canSubmitRegister"
              @click="handleRegister"
            >
              <i v-if="!loading" class="el-icon-right login-btn__icon"></i>
              注册
            </el-button>
          </el-form-item>
          <p class="register-to-login">
            已有账号？
            <router-link class="login-agree-link" to="/login">去登录</router-link>
          </p>
        </div>
      </div>
      <div class="login-agree-outer">
        <el-form-item prop="agreed" class="login-agree-item">
          <el-checkbox v-model="form.agreed">
            <span class="login-agree-text">我已阅读并同意</span>
            <router-link
              class="login-agree-link"
              to="/user-agreement"
              @click.native.stop
            >《用户协议》</router-link>
            <span class="login-agree-text">与</span>
            <router-link
              class="login-agree-link"
              to="/privacy-policy"
              @click.native.stop
            >《隐私政策》</router-link>
          </el-checkbox>
        </el-form-item>
      </div>
    </el-form>
  </div>
</template>

<script>
import axios from 'axios'
import { getApiErrorMessage } from '@/utils/apiErrorMessage'
import {
  PHONE_COUNTRY_OPTIONS,
  buildPhoneForSubmit,
  validatePhoneDigits
} from '@/utils/phoneCountry'

export default {
  name: 'RegisterPage',
  data() {
    const validateConfirm = (_, value, callback) => {
      const s = value != null ? String(value) : ''
      if (!s) {
        callback(new Error('请再次输入密码'))
        return
      }
      if (s !== this.form.password) {
        callback(new Error('两次输入的密码不一致'))
        return
      }
      callback()
    }
    const validatePassword = (_, value, callback) => {
      const s = value != null ? String(value) : ''
      if (!s) {
        callback(new Error('请输入密码'))
        return
      }
      callback()
    }
    const validatePhone = (_, value, callback) => {
      const err = validatePhoneDigits(this.form.countryCode, value)
      if (err) {
        callback(new Error(err))
        return
      }
      callback()
    }
    return {
      countryOptions: PHONE_COUNTRY_OPTIONS,
      form: {
        username: '',
        password: '',
        confirmPassword: '',
        email: '',
        countryCode: '86',
        phone: '',
        emailCode: '',
        agreed: false
      },
      rules: {
        username: [
          { required: true, message: '请输入用户名', trigger: 'blur' },
          { min: 2, max: 64, message: '长度为 2～64 个字符', trigger: 'blur' }
        ],
        password: [
          { validator: validatePassword, trigger: ['blur', 'change'] }
        ],
        confirmPassword: [{ validator: validateConfirm, trigger: ['blur', 'change'] }],
        email: [
          { required: true, message: '请输入邮箱地址', trigger: 'blur' },
          { type: 'email', message: '请输入正确的邮箱地址', trigger: ['blur', 'change'] }
        ],
        phone: [{ validator: validatePhone, trigger: ['blur', 'change'] }],
        emailCode: [{ required: true, message: '请输入邮箱验证码', trigger: 'blur' }],
        agreed: [
          {
            validator: (_, value, callback) => {
              if (!value) {
                callback(new Error('请阅读并同意用户协议和隐私政策'))
              } else {
                callback()
              }
            },
            trigger: 'change'
          }
        ]
      },
      loading: false,
      sendCodeLoading: false,
      sendCodeCooldown: 0,
      sendCodeTimer: null,
      phoneFieldFocused: false
    }
  },
  computed: {
    canSendCode() {
      const username = (this.form.username || '').trim()
      const email = (this.form.email || '').trim()
      const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
      return !!username && emailOk && !this.sendCodeLoading && this.sendCodeCooldown === 0
    },
    sendCodeText() {
      if (this.sendCodeCooldown > 0) return `${this.sendCodeCooldown}s后重发`
      return '发送验证码'
    },
    canSubmitRegister() {
      const username = (this.form.username || '').trim()
      const password = this.form.password || ''
      const email = (this.form.email || '').trim()
      const emailCode = (this.form.emailCode || '').trim()
      const phone = this.buildPhoneForSubmit()
      const emailOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
      return !!username && !!password && !!emailCode && !!phone && emailOk && !this.loading
    }
  },
  beforeDestroy() {
    if (this.sendCodeTimer) {
      window.clearInterval(this.sendCodeTimer)
      this.sendCodeTimer = null
    }
  },
  methods: {
    onPhoneInput(val) {
      const digits = String(val != null ? val : '').replace(/\D/g, '')
      if (digits !== this.form.phone) {
        this.form.phone = digits
      }
    },
    onCountryCodeChange() {
      this.$nextTick(() => {
        if (this.$refs.registerForm) {
          this.$refs.registerForm.validateField('phone')
        }
      })
    },
    onCountrySelectVisible(visible) {
      this.phoneFieldFocused = !!visible
    },
    buildPhoneForSubmit() {
      return buildPhoneForSubmit(this.form.countryCode, this.form.phone)
    },
    startSendCodeCooldown(seconds = 60) {
      this.sendCodeCooldown = seconds
      if (this.sendCodeTimer) {
        window.clearInterval(this.sendCodeTimer)
      }
      this.sendCodeTimer = window.setInterval(() => {
        if (this.sendCodeCooldown <= 1) {
          window.clearInterval(this.sendCodeTimer)
          this.sendCodeTimer = null
          this.sendCodeCooldown = 0
          return
        }
        this.sendCodeCooldown -= 1
      }, 1000)
    },
    async handleSendEmailCode() {
      const username = (this.form.username || '').trim()
      const email = (this.form.email || '').trim()
      if (!username) {
        this.$message.warning('请先输入用户名')
        return
      }
      if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        this.$message.warning('请输入正确的邮箱地址')
        return
      }
      this.sendCodeLoading = true
      try {
        await axios.post(
          '/api/app/email/send-code',
          { email, type: 1 },
          { skipAuth: true }
        )
        this.$message.success('验证码发送成功，请查收邮箱')
        this.startSendCodeCooldown(60)
      } catch (error) {
        this.$message.error(getApiErrorMessage(error, '验证码发送失败，请稍后重试'))
      } finally {
        this.sendCodeLoading = false
      }
    },
    handleRegister() {
      this.$refs.registerForm.validate(async (valid) => {
        if (!valid) return

        const phone = this.buildPhoneForSubmit()
        if (!phone) {
          this.$message.warning('请输入正确的手机号')
          return
        }

        this.loading = true
        try {
          await axios.post(
            '/api/app/core/user/register',
            {
              username: this.form.username.trim(),
              password: this.form.password,
              email: this.form.email.trim(),
              phone,
              verificationCode: this.form.emailCode.trim()
            },
            { skipAuth: true }
          )
          this.$message.success('注册成功，请登录')
          this.$router.replace('/login')
        } catch (error) {
          console.error('注册失败:', error)
          this.$message.error(getApiErrorMessage(error, '注册失败，请重试'))
        } finally {
          this.loading = false
        }
      })
    }
  }
}
</script>

<style scoped>
.login-page {
  min-height: 100vh;
  min-height: 100dvh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: #f2f6ff;
  padding-top: env(safe-area-inset-top, 0px);
  padding-bottom: env(safe-area-inset-bottom, 0px);
  padding-left: max(32px, env(safe-area-inset-left, 0px));
  padding-right: max(32px, env(safe-area-inset-right, 0px));
  box-sizing: border-box;
}

.login-form-wrap {
  width: 100%;
  max-width: 400px;
  margin: 0 auto;
}

.login-content {
  width: 100%;
  max-width: 400px;
  margin: 0 auto;
  padding: 40px 36px 44px;
  box-sizing: border-box;
  background: #ffffff;
  border-radius: 18px;
  border: 1px solid rgba(10, 28, 68, 0.06);
  box-shadow:
    0 4px 24px rgba(10, 28, 68, 0.06),
    0 1px 3px rgba(10, 28, 68, 0.04);
  animation: loginReveal 0.5s ease forwards;
}

@keyframes loginReveal {
  from {
    opacity: 0;
    transform: translateY(16px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.login-title {
  margin: 0 0 10px;
  font-size: 32px;
  font-weight: 600;
  color: #0a1c44;
  letter-spacing: 0.02em;
}

.login-subtitle {
  margin: 0 0 36px;
  font-size: 15px;
  color: #6b7a94;
  font-weight: 400;
  line-height: 1.5;
}

.login-form >>> .el-form-item {
  margin-bottom: 24px;
}

.login-form >>> .el-form-item:last-child {
  margin-bottom: 0;
  margin-top: 8px;
}

.register-to-login {
  margin: 18px 0 0;
  text-align: center;
  font-size: 14px;
  color: #6b7a94;
  line-height: 1.5;
}

.login-agree-outer {
  margin-top: 20px;
  padding: 0 4px;
}

.login-agree-item {
  margin-bottom: 0 !important;
}

.login-agree-item >>> .el-form-item__content {
  line-height: 1.55;
}

.login-agree-item >>> .el-checkbox {
  align-items: flex-start;
  white-space: normal;
}

.login-agree-item >>> .el-checkbox__label {
  font-size: 13px;
  color: #6b7a94;
  line-height: 1.55;
  padding-left: 8px;
}

.login-agree-link {
  color: #4253ff;
  text-decoration: none;
  font-weight: 500;
}

.login-agree-link:hover {
  text-decoration: underline;
}

.login-agree-text {
  color: #6b7a94;
}

.login-input >>> .el-input__inner {
  height: 50px;
  line-height: 50px;
  padding-left: 42px;
  border-radius: 12px;
  background: #f4f6fa;
  border: 1px solid rgba(10, 28, 68, 0.08);
  font-size: 14px;
  color: #0a1c44;
  transition: border-color 0.25s, box-shadow 0.25s, background 0.25s;
}

.login-input >>> .el-input__inner:hover {
  background: #eef1f6;
  border-color: rgba(10, 28, 68, 0.12);
}

.login-input >>> .el-input__prefix {
  left: 12px;
  color: #8b95a8;
}

.login-input >>> .el-input__prefix .el-input__icon {
  font-size: 18px;
  line-height: 50px;
}

.login-input >>> .el-input__inner::placeholder {
  font-size: 14px;
  color: #9aa5b8;
}

.login-input >>> .el-input__inner:focus {
  background: #ffffff;
  border-color: #4253ff;
  box-shadow: 0 0 0 3px rgba(66, 83, 255, 0.12);
}

.register-phone-item {
  margin-bottom: 24px;
}

.register-phone-field {
  display: flex;
  align-items: center;
  width: 100%;
  height: 50px;
  box-sizing: border-box;
  padding: 0 8px 0 12px;
  border-radius: 12px;
  background: #f4f6fa;
  border: 1px solid rgba(10, 28, 68, 0.08);
  transition: border-color 0.25s, box-shadow 0.25s, background 0.25s;
}

.register-phone-field:hover {
  background: #eef1f6;
  border-color: rgba(10, 28, 68, 0.12);
}

.register-phone-field.is-focused {
  background: #ffffff;
  border-color: #4253ff;
  box-shadow: 0 0 0 3px rgba(66, 83, 255, 0.12);
}

.register-phone-field__icon {
  flex: 0 0 auto;
  width: 22px;
  font-size: 18px;
  color: #8b95a8;
  text-align: center;
  line-height: 1;
}

.register-country-select {
  flex: 0 0 auto;
  width: 54px;
}

.register-country-select >>> .el-input__inner {
  height: 46px;
  line-height: 46px;
  padding: 0 14px 0 0;
  border: none;
  background: transparent;
  box-shadow: none;
  font-size: 13px;
  font-weight: 500;
  color: #0a1c44;
  text-align: center;
}

.register-country-select >>> .el-input__inner:hover,
.register-country-select >>> .el-input.is-focus .el-input__inner,
.register-country-select >>> .el-input__inner:focus {
  background: transparent;
  border: none;
  box-shadow: none;
}

.register-country-select >>> .el-input__suffix {
  right: 0;
}

.register-country-select >>> .el-input__suffix .el-input__icon {
  line-height: 46px;
  width: 18px;
  color: #8b95a8;
}

.register-phone-field__divider {
  flex: 0 0 auto;
  width: 1px;
  height: 18px;
  margin: 0 6px 0 2px;
  background: rgba(10, 28, 68, 0.12);
}

.register-phone-input {
  flex: 1;
  min-width: 0;
}

.register-phone-input >>> .el-input__inner {
  height: 46px;
  line-height: 46px;
  padding-left: 8px;
  padding-right: 30px;
  border: none;
  background: transparent;
  box-shadow: none;
  font-size: 14px;
  color: #0a1c44;
}

.register-phone-input >>> .el-input__inner:hover,
.register-phone-input >>> .el-input__inner:focus {
  background: transparent;
  border: none;
  box-shadow: none;
}

.register-phone-input >>> .el-input__inner::placeholder {
  font-size: 14px;
  color: #9aa5b8;
}

.register-phone-input >>> .el-input__suffix .el-input__icon {
  line-height: 46px;
}

.register-code-item {
  margin-bottom: 10px;
}

.register-code-row {
  display: flex;
  align-items: center;
  gap: 10px;
}

.register-code-input {
  flex: 1;
}

.register-send-code-btn {
  flex: 0 0 auto;
  min-width: 116px;
  height: 50px;
  border-radius: 12px;
  padding: 0 12px;
}

.login-btn {
  width: 100%;
  min-height: 50px;
  height: 50px;
  font-size: 16px;
  font-weight: 600;
  letter-spacing: 0.12em;
  border-radius: 12px;
  border: none !important;
  background: linear-gradient(135deg, #4253ff 0%, #3546e8 100%) !important;
  color: #fff !important;
  transition: transform 0.2s, box-shadow 0.2s, opacity 0.2s;
  -webkit-tap-highlight-color: transparent;
}

.login-btn:hover,
.login-btn:focus {
  background: linear-gradient(135deg, #5364ff 0%, #4253ff 100%) !important;
  color: #fff !important;
}

.login-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 8px 20px rgba(66, 83, 255, 0.35);
}

.login-btn:active {
  transform: translateY(0);
}

.login-btn__icon {
  margin-right: 8px;
  font-size: 16px;
  vertical-align: middle;
}

@media (max-width: 768px) {
  .login-page {
    padding-top: max(20px, env(safe-area-inset-top, 0px));
    padding-bottom: max(20px, env(safe-area-inset-bottom, 0px));
    padding-left: max(28px, env(safe-area-inset-left, 0px));
    padding-right: max(28px, env(safe-area-inset-right, 0px));
    align-items: center;
    justify-content: center;
  }

  .login-content {
    max-width: 100%;
    padding: 28px 22px 32px;
    border-radius: 16px;
  }

  .login-title {
    font-size: 26px;
  }

  .login-subtitle {
    font-size: 14px;
    margin-bottom: 28px;
  }

  .login-form >>> .el-form-item {
    margin-bottom: 20px;
  }

  .login-input >>> .el-input__inner {
    height: 48px;
    line-height: 48px;
    padding-left: 40px;
    font-size: 14px;
  }

  .login-input >>> .el-input__prefix .el-input__icon {
    line-height: 48px;
    font-size: 17px;
  }

  .login-btn {
    min-height: 48px;
    height: 48px;
  }

  .register-phone-field {
    height: 48px;
  }

  .register-country-select {
    width: 50px;
  }

  .register-country-select >>> .el-input__inner,
  .register-phone-input >>> .el-input__inner {
    height: 44px;
    line-height: 44px;
  }

  .register-country-select >>> .el-input__suffix .el-input__icon,
  .register-phone-input >>> .el-input__suffix .el-input__icon {
    line-height: 44px;
  }

  .register-send-code-btn {
    height: 48px;
    min-width: 104px;
    font-size: 13px;
  }
}

@media (max-width: 375px) {
  .login-content {
    padding: 24px 18px 28px;
  }

  .login-title {
    font-size: 24px;
  }

  .register-country-select {
    width: 48px;
  }
}
</style>

<style>
.register-country-dropdown.el-select-dropdown {
  border-radius: 12px;
  border: 1px solid rgba(10, 28, 68, 0.08);
  box-shadow: 0 10px 28px rgba(10, 28, 68, 0.12);
  overflow: hidden;
}

.register-country-dropdown .el-select-dropdown__item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 40px;
  line-height: 40px;
  padding: 0 16px;
  font-size: 14px;
}

.register-country-dropdown .el-select-dropdown__item.hover,
.register-country-dropdown .el-select-dropdown__item:hover {
  background: #f4f6fa;
}

.register-country-dropdown .el-select-dropdown__item.selected {
  color: #4253ff;
  font-weight: 600;
  background: rgba(66, 83, 255, 0.06);
}

.register-country-option__label {
  color: inherit;
  font-size: 14px;
}

.register-country-option__dial {
  color: #6b7a94;
  font-size: 13px;
  margin-left: 16px;
  font-variant-numeric: tabular-nums;
}

.register-country-dropdown .el-select-dropdown__item.selected .register-country-option__dial {
  color: #4253ff;
}
</style>
